/**
 * Platform Usage → Guest trials.
 *
 * The admin switch for Guest Trial Mode (`guest_mode_enabled`, fail-closed —
 * off until someone turns it on). Server side is `routes/admin/guest-mode.ts`:
 * admin-only, audited (`guest.mode_changed`), and the only writer of the switch.
 *
 * Deliberate UI rules:
 *   - Pessimistic. The switch always shows the SERVER's state (the GET reads
 *     through the mint path's cache); a toggle disables it until the PUT
 *     answers with the stored state.
 *   - Turning ON asks for confirmation and names the environment. Turning OFF
 *     never does — the off direction must always be one click.
 *   - The ad-click requirement is shown read-only. There is no write path for
 *     it on purpose: turning it off would let organic visitors mint trials.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AdminGuestModeState } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';

/** Human name for the build's `VITE_ENVIRONMENT` (set to `staging` / `prod` by the deploy workflows). */
export function environmentLabel(raw: string | undefined): string {
  switch (raw) {
    case 'prod':
    case 'production':
      return 'production';
    case 'staging':
      return 'staging';
    case undefined:
    case '':
    case 'development':
    case 'dev':
    case 'local':
      return 'local development';
    default:
      return raw;
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function LastChanged({ state }: { state: AdminGuestModeState }) {
  if (!state.updatedAt) {
    return <>Never changed on this environment (guest mode is off by default).</>;
  }
  return (
    <>
      Last changed {new Date(state.updatedAt).toLocaleString()}
      {state.updatedBy && (
        <>
          {' '}
          by <span className="font-mono">{state.updatedBy}</span>
        </>
      )}
      .
    </>
  );
}

export function GuestTrialsTab() {
  const qc = useQueryClient();
  const environment = environmentLabel(import.meta.env.VITE_ENVIRONMENT as string | undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const guestMode = useQuery({
    queryKey: queryKeys.adminGuestMode.state(),
    queryFn: () => api.adminGetGuestMode(),
  });

  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.adminSetGuestModeEnabled(enabled),
    onSuccess: (data) => {
      // Pessimistic: the cache takes the server's answer, never the click.
      qc.setQueryData(queryKeys.adminGuestMode.state(), data);
      toast({ title: data.enabled ? 'Guest mode turned on' : 'Guest mode turned off' });
    },
    onError: (err) => {
      toast({
        title: 'Could not change guest mode',
        description: errorMessage(err),
        variant: 'destructive',
      });
      void qc.invalidateQueries({ queryKey: queryKeys.adminGuestMode.state() });
    },
  });

  function handleToggle(checked: boolean) {
    if (checked) {
      setConfirmOpen(true);
      return;
    }
    // Off is always one click.
    setEnabled.mutate(false);
  }

  const state = guestMode.data;
  const enabled = state?.enabled === true;

  return (
    <div className="max-w-2xl">
      <Card className={enabled ? 'border-amber-500/50' : undefined}>
        <CardHeader>
          <CardTitle>Guest mode</CardTitle>
          <CardDescription>
            When on, visitors who arrive from a verified ad click can start a free trial without an
            account. When off, nobody can.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {guestMode.isLoading ? (
            <div className="animate-pulse space-y-3" aria-label="Loading guest mode">
              <div className="h-5 bg-muted rounded w-40" />
              <div className="h-4 bg-muted rounded w-64" />
            </div>
          ) : guestMode.isError || !state ? (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm space-y-2">
              <p>Could not load the guest mode setting: {errorMessage(guestMode.error)}</p>
              <Button size="sm" variant="outline" onClick={() => void guestMode.refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="guest-mode-enabled" className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={`inline-flex h-2.5 w-2.5 rounded-full ${
                        enabled ? 'bg-amber-500' : 'bg-muted-foreground/40'
                      }`}
                    />
                    Guest mode is{' '}
                    <span data-testid="guest-mode-status" className="font-semibold">
                      {enabled ? 'On' : 'Off'}
                    </span>
                  </Label>
                  <p
                    className="mt-1 text-xs text-muted-foreground"
                    data-testid="guest-mode-changed"
                  >
                    <LastChanged state={state} />
                  </p>
                </div>
                <Switch
                  id="guest-mode-enabled"
                  aria-label="Guest mode"
                  checked={enabled}
                  disabled={setEnabled.isPending}
                  onCheckedChange={handleToggle}
                />
              </div>

              <div className="rounded-lg border border-border p-3 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-foreground">Ad click required</span>
                  <span data-testid="guest-ad-click-required" className="font-semibold">
                    {state.adClickRequired ? 'Yes' : 'No'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {state.adClickRequired
                    ? 'Only visitors with a verified ad click can start a trial. Read-only here.'
                    : 'Any visitor can start a trial while guest mode is on. Read-only here; it was changed outside the console.'}
                </p>
              </div>

              {state.devModeBypass && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Local development: the server skips this switch and the ad-click requirement, so
                  guests can always start a trial here whatever it shows.
                </p>
              )}

              <p className="text-xs text-muted-foreground">
                {setEnabled.isPending
                  ? 'Saving…'
                  : 'Takes effect on this server immediately; other servers pick it up within about 30 seconds. Trials already started keep working until they expire.'}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on guest mode on {environment}?</AlertDialogTitle>
            <AlertDialogDescription>
              Visitors who arrive from a verified ad click will be able to start a free trial on{' '}
              {environment} without an account, spending trial credits. You can turn it off again
              with one click.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEnabled.mutate(true)}>
              Turn on guest mode
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
